import Link from 'next/link';
import { Info } from 'lucide-react';

export default function AirconPricing(props) {
    const { standardServicing, annualServicing, chemicalServicing, overhaulServicing, gasTopup, currencyCd } = props;
    if (!standardServicing || !annualServicing || !chemicalServicing || !overhaulServicing || !gasTopup) {
        return (
            <div className="max-w-6xl mx-auto my-20 p-6 text-center">
                <p className="text-lg text-gray-600">Pricing data is currently unavailable. Please check back later.</p>
            </div>
        );  
    }

  return (
    <section id="price-list" className="max-w-6xl mx-auto my-10 space-y-12 p-6 rounded-xl bg-white shadow-lg">
        {/* General Servicing */}
        {standardServicing.length === 0 || annualServicing.length === 0 ?
            <div className="text-center text-gray-600">
                No standard aircon servicing pricing available at the moment.
            </div>
            :
            <div className="space-y-6">
                <header className="text-center">
                <h2 className="text-2xl md:text-3xl font-bold uppercase text-gray-800">
                    General Aircon Service Pricing
                </h2>
                <p className="text-gray-600 max-w-3xl mx-auto mt-2">
                    StringsSG offers the most affordable and transparent aircon servicing pricing in Singapore.
                </p>
                </header>
                <div className="grid md:grid-cols-2 gap-6">
                <PricingTable title="One-Time Servicing" data={standardServicing} columns={["No. of Aircons", "Pricing"]} lastRowLabel="more than 5" currencyCd={currencyCd} />
                <PricingTable title="Annual Package" data={annualServicing} columns={["No. of Aircons", "4 times/year", "3 times/year"]} lastRowLabel="more than 5" annual currencyCd={currencyCd} />
                </div>
            </div>
        }

        {/* Gas Top-Up */}
        {gasTopup.length === 0 ? 
            <div className="text-center text-gray-600"> 
                No gas top-up pricing available at the moment.
            </div>
        :
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-center text-orange-600">
                Gas Top-Up Pricing
                </h3>
                <div className="flex justify-center">
                <table className="w-full sm:w-1/2 text-sm text-gray-700 border">
                    <thead className="bg-amber-100 text-amber-800 uppercase">
                    <tr>
                        <th className="p-3 border-b">Gas Type</th>
                        <th className="p-3 border-b">Price (SGD)</th>
                    </tr>
                    </thead>
                    <tbody>
                    {gasTopup.map((item, i) => (
                        <tr key={i} className="hover:bg-white transition">
                        <td className="p-3 border-b text-center">{item.type}</td>
                        {/* priceRange already carries its currency ("S$40 - S$100"); prefixing
                            currencyCd rendered "SGD S$40 - S$100". */}
                        <td className="p-3 border-b text-center">{item.priceRange}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                </div>
            </div>
        }

        {/* Chemical Servicing */}
        {(chemicalServicing.length === 0 || overhaulServicing.length === 0) ?
            <div className="text-center text-gray-600">
                No chemical servicing pricing available at the moment.
            </div>
        :
            <div className="space-y-6">
                <header className="text-center">
                <h2 className="text-2xl md:text-3xl font-bold uppercase text-gray-800">
                    Aircon Chemical Wash Pricing
                </h2>
                <p className="text-gray-600 max-w-3xl mx-auto mt-2">
                    Ensure your air conditioner is clean and germ-free with our annual chemical wash.
                    Learn more about
                    <Link
                    href="/sg/aircon-servicing-singapore/aircon-chemical-wash-and-overhaul"
                    prefetch={false}
                    className="inline-flex items-center gap-1 px-3 py-1.5 ml-1 rounded-md text-orange-600 hover:bg-amber-50 transition shadow"
                    >
                    <Info className="w-4 h-4" /> Basic Chemical Wash & Overhaul
                    </Link>
                </p>
                </header>
                <div className="grid md:grid-cols-2 gap-6">
                    <PricingTable title="Chemical Wash Price" data={chemicalServicing} columns={["No. of Aircons", "Pricing"]} lastRowLabel="more than 5" currencyCd={currencyCd} />
                    <PricingTable title="Chemical Overhaul Price" data={overhaulServicing} columns={["No. of Aircons", "Pricing"]} lastRowLabel="more than 5" currencyCd={currencyCd} />
                </div>
            </div>
        }
    </section>
  );
}

function PricingTable({ title, data, columns, annual = false, lastRowLabel, currencyCd }) {
  return (
    <div className="bg-gray-50 rounded-xl p-5 shadow hover:shadow-md transition">
      <h3 className="text-lg font-semibold text-center text-orange-600 mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-700 border">
          <thead className="bg-amber-100 text-amber-800 uppercase">
            <tr>
              {columns.map((col, i) => (
                <th key={i} className="p-3 border-b">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((tran, i) => (
              <tr key={i} className="hover:bg-white transition">
                <td className="p-3 border-b text-center">{tran.number}</td>
                {annual ? (
                  <>
                    <td className="p-3 border-b text-center">{tran.currencyCd} {tran.threeMonthInterval}</td>
                    <td className="p-3 border-b text-center">{tran.currencyCd} {tran.fourMonthInterval}</td>
                  </>
                ) : (
                  <td className="p-3 border-b text-center">{tran.currencyCd} {tran.rate}</td>
                )}
              </tr>
            ))}
            <tr className="bg-white font-medium">
              <td className="p-3 border-t text-center">{lastRowLabel}</td>
              {annual ? (
                <>
                  <td className="p-3 border-t text-center text-amber-700">Open a request</td>
                  <td className="p-3 border-t text-center text-amber-700">Open a request</td>
                </>
              ) : (
                <td className="p-3 border-t text-center text-amber-700">Open a request</td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}